import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { 
  Rocket, ChevronRight, ChevronDown, Folder, FolderOpen, 
  Plus, Link as LinkIcon, ExternalLink, Users, Lock,
  Edit, Trash2, Share2, MoreVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { launchpadApi } from '../../lib/api';

const MobileLaunchpad = forwardRef(({
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onShareFolder,
  onAddLink,
  onEditLink,
  onDeleteLink,
  onClose // To close mobile menu after action
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('launchpad_mobile_expanded');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('launchpad_expanded_folders');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [folderLinks, setFolderLinks] = useState({});
  const [loadingFolders, setLoadingFolders] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch folders on mount and when expanded
  const fetchFolders = useCallback(async () => {
    if (!isExpanded) return;
    setLoading(true);
    try {
      const response = await launchpadApi.getFolders();
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Failed to fetch launchpad folders:', error);
    } finally {
      setLoading(false);
    }
  }, [isExpanded]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Fetch folder links when folder is expanded
  const fetchFolderLinks = useCallback(async (folderId, forceRefresh = false) => {
    if (!forceRefresh && (folderLinks[folderId] || loadingFolders[folderId])) return;
    setLoadingFolders(prev => ({ ...prev, [folderId]: true }));
    try {
      const response = await launchpadApi.getFolder(folderId);
      setFolderLinks(prev => ({ ...prev, [folderId]: response.data.links || [] }));
    } catch (error) {
      console.error('Failed to fetch folder links:', error);
      setFolderLinks(prev => ({ ...prev, [folderId]: [] }));
    } finally {
      setLoadingFolders(prev => ({ ...prev, [folderId]: false }));
    }
  }, [folderLinks, loadingFolders]);

  // Toggle handlers
  const toggleLaunchpad = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    try {
      localStorage.setItem('launchpad_mobile_expanded', newState.toString());
    } catch (e) {}
  };

  const toggleFolder = (folderId) => {
    const isExpanding = !expandedFolders[folderId];
    const newState = { ...expandedFolders, [folderId]: isExpanding };
    setExpandedFolders(newState);
    try {
      localStorage.setItem('launchpad_expanded_folders', JSON.stringify(newState));
    } catch (e) {}
    if (isExpanding) {
      fetchFolderLinks(folderId);
    }
  };

  // Refresh methods
  const refreshFolders = useCallback(() => {
    setFolders([]);
    setFolderLinks({});
    fetchFolders();
  }, [fetchFolders]);

  const refreshFolderLinks = useCallback((folderId) => {
    if (folderId) {
      fetchFolderLinks(folderId, true);
    }
  }, [fetchFolderLinks]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    refreshFolders,
    refreshFolderLinks
  }), [refreshFolders, refreshFolderLinks]);

  // Open link in new tab
  const handleOpenLink = (url, e) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  // Check if user can edit
  const canEdit = (folder) => {
    return folder.user_permission === 'owner' || folder.user_permission === 'edit';
  };

  return (
    <div className="border-t border-border pt-3 mt-2">
      {/* Launchpad Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer hover:bg-muted"
        onClick={toggleLaunchpad}
        data-testid="mobile-launchpad-toggle"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          )}
          <Rocket className="w-5 h-5 text-muted-foreground" />
          <span className="font-medium">Launchpad</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-[#4A9CC7]"
          onClick={(e) => {
            e.stopPropagation();
            onCreateFolder?.();
            onClose?.();
          }}
          title="Create Folder"
          data-testid="mobile-launchpad-create-folder-btn"
        >
          <Plus className="w-5 h-5" />
        </Button>
      </div>

      {/* Launchpad Content */}
      {isExpanded && (
        <div className="mt-1 space-y-1 px-2">
          {loading ? (
            <div className="text-sm text-muted-foreground px-4 py-3">Loading...</div>
          ) : folders.length === 0 ? (
            <div className="text-sm text-muted-foreground px-4 py-3">
              No folders yet. Tap + to create one.
            </div>
          ) : (
            folders.map(folder => (
              <div key={folder.launchpad_folder_id} className="rounded-lg overflow-hidden">
                {/* Folder Row */}
                <div 
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted/80 cursor-pointer"
                  onClick={() => toggleFolder(folder.launchpad_folder_id)}
                  data-testid={`mobile-launchpad-folder-${folder.launchpad_folder_id}`}
                >
                  {expandedFolders[folder.launchpad_folder_id] ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span style={{ color: folder.color || '#6B7280' }}>
                    {expandedFolders[folder.launchpad_folder_id] ? (
                      <FolderOpen className="w-5 h-5" />
                    ) : (
                      <Folder className="w-5 h-5" />
                    )}
                  </span>
                  <span className="text-sm font-medium truncate flex-1">{folder.name}</span>
                  
                  {/* Folder type indicator */}
                  {folder.folder_type === 'shared' && (
                    <Users className="w-4 h-4 text-muted-foreground" />
                  )}
                  {folder.folder_type === 'personal' && folder.user_permission === 'owner' && (
                    <Lock className="w-4 h-4 text-muted-foreground opacity-50" />
                  )}
                  
                  {/* Actions Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      {canEdit(folder) && (
                        <DropdownMenuItem onClick={() => { onAddLink?.(folder); onClose?.(); }}>
                          <LinkIcon className="w-4 h-4 mr-2" />
                          Add Link
                        </DropdownMenuItem>
                      )}
                      {folder.user_permission === 'owner' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { onEditFolder?.(folder); onClose?.(); }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Folder
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { onShareFolder?.(folder); onClose?.(); }}>
                            <Share2 className="w-4 h-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => { onDeleteFolder?.(folder); onClose?.(); }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Links inside folder */}
                {expandedFolders[folder.launchpad_folder_id] && (
                  <div className="ml-4 pl-4 border-l-2 border-border space-y-1 pb-2">
                    {loadingFolders[folder.launchpad_folder_id] ? (
                      <div className="text-sm text-muted-foreground px-3 py-2">Loading...</div>
                    ) : (folderLinks[folder.launchpad_folder_id] || []).length === 0 ? (
                      <div className="text-sm text-muted-foreground px-3 py-2">No links yet</div>
                    ) : (
                      (folderLinks[folder.launchpad_folder_id] || []).map(link => (
                        <div 
                          key={link.link_id}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted active:bg-muted/80 cursor-pointer group"
                          onClick={(e) => handleOpenLink(link.url, e)}
                          data-testid={`mobile-launchpad-link-${link.link_id}`}
                        >
                          {link.favicon_url ? (
                            <img 
                              src={link.favicon_url} 
                              alt="" 
                              className="w-4 h-4 rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <LinkIcon className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="text-sm truncate flex-1">{link.title}</span>
                          <ExternalLink className="w-4 h-4 text-muted-foreground opacity-50" />
                          
                          {/* Link actions */}
                          {canEdit(folder) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-36">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditLink?.(link, folder); onClose?.(); }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={(e) => { e.stopPropagation(); onDeleteLink?.(link, folder); onClose?.(); }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      ))
                    )}
                    
                    {/* Add link button */}
                    {canEdit(folder) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start h-10 px-3 text-sm text-muted-foreground hover:text-[#4A9CC7]"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddLink?.(folder);
                          onClose?.();
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Link
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
});

MobileLaunchpad.displayName = 'MobileLaunchpad';

export default MobileLaunchpad;
